import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { AdminNav } from '../../components/AdminNav';

export const AdminDeletion = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const { token, isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    fetchDeletionSchedule();
  }, [isAuthenticated, navigate]);

  const fetchDeletionSchedule = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/deletion-schedule`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch deletion schedule');

      const data = await response.json();
      setAuctions(data.auctions);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerScheduler = async () => {
    setTriggerLoading(true);
    setSuccess('');
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/deletion/run-scheduler`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to trigger scheduler');

      const data = await response.json();
      setSuccess('Scheduler executed successfully! Check logs for details.');
      
      // Refresh the deletion schedule
      setTimeout(() => fetchDeletionSchedule(), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setTriggerLoading(false);
    }
  };

  const handlePauseDeletion = async (auctionId, pause) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/auctions/${auctionId}/pause-deletion?pause=${pause}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to update deletion status');

      setSuccess(`Deletion ${pause ? 'paused' : 'resumed'} successfully`);
      fetchDeletionSchedule();
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'warning_30': { color: 'yellow', text: '30-Day Warning', emoji: '⚠️' },
      'warning_14': { color: 'orange', text: '14-Day Warning', emoji: '🔔' },
      'warning_3': { color: 'red', text: '3-Day Warning', emoji: '🚨' },
      'confirmed': { color: 'green', text: 'Confirmed', emoji: '✅' },
      'none': { color: 'gray', text: 'Not Contacted', emoji: '📝' },
    };

    const config = statusConfig[status] || statusConfig['none'];

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <span className="mr-1">{config.emoji}</span>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Deletion Schedule</h2>
          <button
            onClick={handleTriggerScheduler}
            disabled={triggerLoading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition disabled:opacity-50"
          >
            {triggerLoading ? 'Running...' : '▶️ Run Scheduler Now'}
          </button>
        </div>

        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <p className="text-green-700">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading deletion schedule...</p>
          </div>
        ) : auctions.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <span className="text-6xl mb-4 block">📋</span>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Auctions in Deletion Schedule</h3>
            <p className="text-gray-600">All auctions are either confirmed or not yet contacted.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auction Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Contacted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deletion Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auctions.map((auction) => (
                  <tr key={auction.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{auction.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{auction.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(auction.deletion_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(auction.last_contacted_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(auction.deletion_scheduled_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {auction.deletion_paused ? (
                        <button
                          onClick={() => handlePauseDeletion(auction.id, false)}
                          className="text-green-600 hover:text-green-900 font-medium"
                        >
                          ▶️ Resume
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePauseDeletion(auction.id, true)}
                          className="text-yellow-600 hover:text-yellow-900 font-medium"
                        >
                          ⏸️ Pause
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <span className="text-2xl mr-3">💡</span>
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">Deletion System Info</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Auctions receive warnings at 30, 14, and 3 days before deletion</li>
                <li>• Auctioneers can confirm their listing via email link to prevent deletion</li>
                <li>• Paused auctions won't receive warnings or be deleted</li>
                <li>• The scheduler runs automatically daily at 9:00 AM</li>
                <li>• Archived auctions are kept for 30 days before permanent deletion</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
